import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from './shell/AppShell';
import BulkActionBar from './shell/BulkActionBar';
import TaskInbox from './TaskInbox';
import OutreachAnalytics from './OutreachAnalytics';
import OutboxTab from './OutboxTab';
import DncTab from './DncTab';
import ConversationsTab from './ConversationsTab';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

// Tiny inline sparkline (14-day sent trend). Pure SVG, no deps.
const Sparkline = ({ data, color = '#534AB7' }) => {
  const pts = Array.isArray(data) ? data : [];
  if (pts.length === 0 || pts.every(v => !v)) {
    return <span className="text-[10px] text-gray-300">—</span>;
  }
  const w = 64, h = 20, max = Math.max(...pts, 1);
  const step = pts.length > 1 ? w / (pts.length - 1) : w;
  const path = pts.map((v, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${(h - (v / max) * (h - 2) - 1).toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} className="overflow-visible">
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

// A metric cell: big number + small subvalue (rate % or raw count).
const Metric = ({ value, sub, tone }) => (
  <div className="text-right tabular-nums">
    <div className={`text-sm font-semibold ${tone || 'text-gray-900'}`}>{value}</div>
    {sub != null && <div className="text-[10px] text-gray-400">{sub}</div>}
  </div>
);

const pct = (num, den) => (den > 0 ? `${Math.round((num / den) * 100)}%` : '—');

// Sortable column header — click to sort, shows an up/down caret when active.
const SortHeader = ({ col, label, align, sortBy, sortDir, onSort }) => {
  const active = sortBy === col;
  return (
    <button onClick={() => onSort(col)}
      className={`flex items-center gap-0.5 select-none uppercase tracking-wide hover:text-emerald-900 transition-colors ${align === 'right' ? 'justify-end text-right' : 'justify-start text-left'}`}
      title={`Sort by ${label}`}>
      <span>{label}</span>
      <svg className={`w-3 h-3 flex-shrink-0 transition-opacity ${active ? 'opacity-100' : 'opacity-25'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {active && sortDir === 'asc'
          ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
          : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />}
      </svg>
    </button>
  );
};

const OutreachPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    // 'sequences' tab was removed (folded into the campaign's Enrolled tab).
    return (!t || t === 'sequences') ? 'campaigns' : t;
  });
  const [unreadReplies, setUnreadReplies] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [viewMode, setViewModeState] = useState(() => {
    try { return localStorage.getItem('outreach_view_mode') === 'cards' ? 'cards' : 'table'; }
    catch { return 'table'; }
  });
  const setViewMode = (mode) => {
    setViewModeState(mode);
    try { localStorage.setItem('outreach_view_mode', mode); } catch {}
  };
  // Toggleable grid columns (checkbox / campaign / actions are always shown).
  const COLUMNS = [
    { key: 'trend', label: 'Trend', width: '90px', sortable: false },
    { key: 'owner', label: 'Owner', width: '70px', sortable: true },
    { key: 'created', label: 'Created', width: '90px', sortable: true },
    { key: 'enrolled', label: 'In campaign', width: '0.8fr', sortable: true },
    { key: 'messaged', label: 'Messaged', width: '0.8fr', sortable: true },
    { key: 'open', label: 'Open', width: '0.8fr', sortable: true },
    { key: 'reply', label: 'Reply', width: '0.8fr', sortable: true },
    { key: 'click', label: 'Click', width: '0.8fr', sortable: true },
  ];
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('outreach_visible_cols') || 'null');
      if (saved) return saved;
    } catch {}
    return { trend: true, owner: true, created: true, enrolled: true, messaged: true, open: true, reply: true, click: true };
  });
  const [showColMenu, setShowColMenu] = useState(false);
  const [sortBy, setSortBy] = useState(null);      // column key
  const [sortDir, setSortDir] = useState('desc');  // 'asc' | 'desc'
  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key); setSortDir('desc');
    }
  };
  const toggleCol = (key) => {
    setVisibleCols(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('outreach_visible_cols', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  // Build the grid-template string from visible columns (fixed cols bookend).
  const gridTemplate = `28px minmax(0,2.2fr) ${COLUMNS.filter(c => visibleCols[c.key]).map(c => c.width).join(' ')} 40px`;
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showScheduleSettings, setShowScheduleSettings] = useState(false);
  const [firmSettings, setFirmSettings] = useState(null);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadCampaigns = async () => {
    try {
      const p = new URLSearchParams();
      if (channelFilter) p.set('channel', channelFilter);
      if (search.trim()) p.set('search', search.trim());
      const qs = p.toString() ? `?${p.toString()}` : '';
      const res = await fetch(`${API}/api/outreach/campaigns${qs}`, { headers });
      if (res.ok) setCampaigns(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Debounce search; refetch on filter changes. Status is filtered
  // client-side (below) so the per-status counts always reflect the full set.
  useEffect(() => {
    const t = setTimeout(loadCampaigns, search ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [channelFilter, search]);

  // Unread SMS-reply count for the Replies tab badge. Polls, and clears when
  // the user opens the tab.
  const loadUnread = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/outreach/conversations/unread-count`, { headers });
      if (res.ok) { const d = await res.json(); setUnreadReplies(d.count || 0); }
    } catch (e) { /* non-fatal */ }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    loadUnread();
    const iv = setInterval(loadUnread, 60000);
    return () => clearInterval(iv);
  }, [loadUnread]);

  useEffect(() => {
    if (activeTab === 'conversations') {
      fetch(`${API}/api/outreach/conversations/mark-read`, { method: 'POST', headers }).catch(() => {});
      setUnreadReplies(0);
    }
    // eslint-disable-next-line
  }, [activeTab]);

  const createCampaign = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/outreach/campaigns`, {
        method: 'POST', headers,
        body: JSON.stringify({ name: newName.trim(), description: newDesc.trim() || null })
      });
      if (res.ok) { const campaign = await res.json(); navigate(`/outreach/${campaign.id}`); }
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  const setCampaignStatus = async (id, status) => {
    try {
      await fetch(`${API}/api/outreach/campaigns/${id}`, {
        method: 'PUT', headers, body: JSON.stringify({ status })
      });
    } catch (e) { console.error(e); }
  };

  // ---- Bulk actions ----
  const runBulk = async (fn) => {
    setBusy(true);
    try { await Promise.all([...selected].map(fn)); }
    finally { setBusy(false); setSelected(new Set()); loadCampaigns(); }
  };
  const bulkPause = () => runBulk(id => setCampaignStatus(id, 'paused'));
  const bulkResume = () => runBulk(id => setCampaignStatus(id, 'active'));
  const bulkArchive = () => {
    if (!window.confirm(`Archive ${selected.size} campaign(s)?`)) return;
    runBulk(id => setCampaignStatus(id, 'archived'));
  };
  const bulkDelete = () => {
    if (!window.confirm(`Permanently delete ${selected.size} campaign(s)? This cannot be undone.`)) return;
    runBulk(id => fetch(`${API}/api/outreach/campaigns/${id}`, { method: 'DELETE', headers }));
  };
  const bulkDuplicate = () => runBulk(id => fetch(`${API}/api/outreach/campaigns/${id}/duplicate`, { method: 'POST', headers }));
  const exportCsv = () => {
    const rows = campaigns.filter(c => selected.has(c.id));
    const header = ['Name', 'Status', 'In campaign', 'Messaged', 'Open rate', 'Reply rate', 'Click rate'];
    const body = rows.map(c => {
      const enrolled = c.total_enrolled || 0, messaged = c.messaged_count || 0;
      return [
        `"${(c.name || '').replace(/"/g, '""')}"`, c.status,
        enrolled, messaged, pct(c.opened_count, messaged),
        pct(c.reply_events, messaged), pct(c.clicked_count, messaged)
      ].join(',');
    });
    const csv = [header.join(','), ...body].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'campaigns.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const toggle = (id) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const lastClickedIndex = React.useRef(null);
  // Toggle with shift-click range support. `list` is the ordered array currently rendered.
  const toggleWithShift = (id, index, shiftKey, list) => {
    if (shiftKey && lastClickedIndex.current !== null && list) {
      const start = Math.min(lastClickedIndex.current, index);
      const end = Math.max(lastClickedIndex.current, index);
      setSelected(prev => {
        const n = new Set(prev);
        // Match the selected-state of the anchor row across the whole range.
        const anchorId = list[lastClickedIndex.current]?.id;
        const shouldSelect = anchorId ? n.has(anchorId) : true;
        for (let i = start; i <= end; i++) {
          const rid = list[i]?.id;
          if (!rid) continue;
          if (shouldSelect) n.add(rid); else n.delete(rid);
        }
        return n;
      });
    } else {
      toggle(id);
    }
    lastClickedIndex.current = index;
  };
  const allSelected = campaigns.length > 0 && campaigns.every(c => selected.has(c.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(campaigns.map(c => c.id)));

  const statusColors = {
    active: 'bg-green-100 text-green-700',
    draft: 'bg-gray-100 text-gray-600',
    paused: 'bg-yellow-100 text-yellow-700',
    archived: 'bg-gray-100 text-gray-400',
  };

  // Faint row wash per status — "Subtle" intensity, no left spine.
  const statCounts = useMemo(() => ({
    all: campaigns.length,
    active: campaigns.filter(c => c.status === 'active').length,
    draft: campaigns.filter(c => c.status === 'draft').length,
    paused: campaigns.filter(c => c.status === 'paused').length,
    archived: campaigns.filter(c => c.status === 'archived').length,
  }), [campaigns]);

  const channelOf = (c) => c.settings?.channel || c.type || 'email';

  // Sorted view of campaigns based on the active sort column.
  const sortedCampaigns = useMemo(() => {
    // Filter by status client-side so per-status counts reflect the full set.
    const base = statusFilter ? campaigns.filter(c => c.status === statusFilter) : campaigns;
    if (!sortBy) return base;
    const val = (c) => {
      switch (sortBy) {
        case 'owner': return (c.created_by_name || '').toLowerCase();
        case 'created': return new Date(c.created_at || 0).getTime();
        case 'enrolled': return c.total_enrolled || 0;
        case 'messaged': return c.messaged_count || 0;
        case 'open': return (c.messaged_count > 0) ? (c.opened_count || 0) / c.messaged_count : -1;
        case 'reply': return (c.messaged_count > 0) ? (c.reply_events || 0) / c.messaged_count : -1;
        case 'click': return (c.messaged_count > 0) ? (c.clicked_count || 0) / c.messaged_count : -1;
        default: return 0;
      }
    };
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...base].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [campaigns, sortBy, sortDir, statusFilter]);

  // Load master scheduling settings when the settings modal opens.
  useEffect(() => {
    if (!showScheduleSettings) return;
    fetch(`${API}/api/outreach/firm-settings`, { headers })
      .then(r => r.ok ? r.json() : null).then(setFirmSettings).catch(() => {});
    // eslint-disable-next-line
  }, [showScheduleSettings]);

  const saveFirmSettings = async () => {
    try {
      await fetch(`${API}/api/outreach/firm-settings`, {
        method: 'PUT', headers, body: JSON.stringify(firmSettings)
      });
      setShowScheduleSettings(false);
    } catch (e) { console.error(e); }
  };

  const tabStrip = (
    <div className="h-10 flex items-stretch px-3 gap-1">
      {[
        { key: 'campaigns', label: 'Campaigns', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /> },
        { key: 'outbox', label: 'Outbox', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h4l2 3h6l2-3h4M5 19h14a2 2 0 002-2v-4a2 2 0 00-.4-1.2l-3-4A2 2 0 0016 7H8a2 2 0 00-1.6.8l-3 4A2 2 0 003 13v4a2 2 0 002 2z" /> },
        { key: 'conversations', label: 'Replies', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.83l-5 1.5 1.5-4.5A7.94 7.94 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /> },
        { key: 'tasks', label: 'Tasks', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /> },
        { key: 'analytics', label: 'Analytics', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /> },
        { key: 'dnc', label: 'Do-Not-Contact', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /> },
      ].map(tab => {
        const on = activeTab === tab.key;
        return (
          <button key={tab.key} onClick={() => { setActiveTab(tab.key); setShowCreateModal(false); setShowScheduleSettings(false); }}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 text-xs font-medium transition-colors border-b-2 -mb-px rounded-t ${
              on
                ? 'text-emerald-700 border-emerald-600'
                : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-100/70'
            }`}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">{tab.icon}</svg>
            {tab.label}
            {tab.key === 'conversations' && unreadReplies > 0 && (
              <span className="ml-0.5 min-w-[16px] h-4 px-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-semibold">
                {unreadReplies > 99 ? '99+' : unreadReplies}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  // Bulk bar replaces the tab strip in place when campaigns are selected.
  const pageStrip = (activeTab === 'campaigns' && selected.size > 0) ? (
    <BulkActionBar
      count={selected.size}
      busy={busy}
      onClear={() => setSelected(new Set())}
      actions={[
        { label: 'Resume', onClick: bulkResume },
        { label: 'Pause', onClick: bulkPause },
        { label: 'Duplicate', onClick: bulkDuplicate },
        { label: 'Archive', onClick: bulkArchive },
        { label: 'Delete', onClick: bulkDelete },
        { label: 'Export CSV', onClick: exportCsv },
      ]}
    />
  ) : tabStrip;

  return (
    <AppShell pageStrip={pageStrip}>
      <div className="h-full flex overflow-hidden" style={{ backgroundColor: 'var(--list-page-bg)' }}>
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Non-campaign tabs manage their own scroll internally */}
        {activeTab === 'tasks' && <div className="flex-1 overflow-y-auto px-3 pt-2 pb-4"><TaskInbox /></div>}
        {activeTab === 'analytics' && <div className="flex-1 overflow-y-auto px-3 pt-2 pb-4"><OutreachAnalytics /></div>}
        {activeTab === 'outbox' && <div className="flex-1 min-h-0 flex flex-col overflow-hidden"><OutboxTab /></div>}
        {activeTab === 'dnc' && <div className="flex-1 min-h-0 flex flex-col overflow-hidden"><DncTab /></div>}
        {activeTab === 'conversations' && <div className="flex-1 min-h-0 px-3 pt-2 pb-3"><ConversationsTab /></div>}

        {activeTab === 'campaigns' && (<>
        {/* Toolbar: fixed at top (flex-shrink-0); only the list below scrolls. */}
        <div className="flex-shrink-0 flex items-center gap-2 flex-wrap px-3 pt-2 pb-2">
          <div className="relative">
            <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
              onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
              className="h-[30px] w-44 pl-7 pr-2 text-sm border border-gray-300 rounded focus:outline-none transition-shadow"
              style={searchFocused ? { boxShadow: '0 0 0 1px rgba(16, 185, 129, 0.15), 0 0 14px 2px rgba(16, 185, 129, 0.22), 0 0 28px 3px rgba(16, 185, 129, 0.10)', borderColor: 'rgba(16, 185, 129, 0.4)' } : undefined} />
          </div>
          <div className="flex items-center gap-1">
            <div className="inline-flex h-[30px] border border-gray-300 rounded overflow-hidden bg-white">
              {[
                { key: '', label: 'All', count: statCounts.all },
                { key: 'active', label: 'Active', count: statCounts.active },
                { key: 'draft', label: 'Drafts', count: statCounts.draft },
                { key: 'paused', label: 'Paused', count: statCounts.paused },
                { key: 'archived', label: 'Archived', count: statCounts.archived },
              ].map((tab, i) => (
                <button key={tab.key} onClick={() => setStatusFilter(tab.key)}
                  className={`px-3 text-sm flex items-center gap-1.5 transition-colors ${i > 0 ? 'border-l border-gray-300' : ''} ${
                    statusFilter === tab.key
                      ? 'bg-emerald-50 text-emerald-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}>
                  {tab.label}{tab.count > 0 && <span className={`text-[10px] ${statusFilter === tab.key ? 'text-emerald-700/70' : 'text-gray-400'}`}>{tab.count}</span>}
                </button>
              ))}
            </div>
          </div>
          {/* View toggle — inline after the status filters */}
          <div className="flex items-center gap-0.5 border border-gray-300 rounded p-0.5" role="tablist" aria-label="View mode">
            <button onClick={() => setViewMode('table')} aria-label="Table view" title="Table view"
              className={`p-1 rounded transition-colors ${viewMode === 'table' ? 'bg-emerald-100 text-emerald-800' : 'text-gray-500 hover:bg-gray-100'}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
            <button onClick={() => setViewMode('cards')} aria-label="Card view" title="Card view"
              className={`p-1 rounded transition-colors ${viewMode === 'cards' ? 'bg-emerald-100 text-emerald-800' : 'text-gray-500 hover:bg-gray-100'}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
            </button>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* Column visibility selector */}
            <div className="relative">
              <button onClick={() => setShowColMenu(v => !v)} title="Show/hide columns"
                className="h-[30px] px-1.5 border border-gray-300 rounded text-gray-500 hover:bg-gray-50 flex items-center gap-0.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                <svg className={`w-3 h-3 transition-transform ${showColMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {showColMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowColMenu(false)} />
                  <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Columns</div>
                    {COLUMNS.map(col => (
                      <button key={col.key} onClick={() => toggleCol(col.key)}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-emerald-50 transition-colors">
                        <input type="checkbox" readOnly checked={!!visibleCols[col.key]}
                          className="w-4 h-4 rounded border-gray-300 accent-emerald-600 pointer-events-none" />
                        {col.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button onClick={() => { setShowScheduleSettings(true); setShowCreateModal(false); }} title="Master scheduling settings"
              className="p-1.5 border border-gray-300 rounded text-gray-500 hover:bg-gray-50">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </button>
            <button onClick={() => { setShowCreateModal(true); setShowScheduleSettings(false); setNewName(''); setNewDesc(''); }}
              className="h-[30px] px-3 bg-emerald-600 text-white text-xs font-medium rounded hover:bg-emerald-700 flex items-center gap-1.5 whitespace-nowrap">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              New Campaign
            </button>
          </div>
        </div>

        {/* Full-width table */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="mx-3 mb-8 bg-white rounded-lg border shadow-sm p-12 text-center">
            <p className="text-gray-500 font-medium">No campaigns found</p>
            <p className="text-sm text-gray-400 mt-1">Adjust your filters, or create a new campaign to start.</p>
          </div>
        ) : viewMode === 'cards' ? (
          <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 pt-1">
              {sortedCampaigns.map((c, idx) => {
                const enrolled = c.total_enrolled || 0;
                const messaged = c.messaged_count || 0;
                const opened = c.opened_count || 0;
                const clicked = c.clicked_count || 0;
                const replied = c.reply_events || 0;
                return (
                  <div key={c.id}
                    className={`bg-white border rounded-lg p-4 hover:shadow-md transition-all flex flex-col gap-3 ${selected.has(c.id) ? 'border-emerald-400 ring-1 ring-emerald-300' : 'border-gray-200'}`}>
                    {/* Header: checkbox + name + status */}
                    <div className="flex items-start gap-3">
                      <input type="checkbox" checked={selected.has(c.id)}
                        onChange={(e) => toggleWithShift(c.id, idx, e.nativeEvent.shiftKey, sortedCampaigns)}
                        className="w-4 h-4 mt-0.5 rounded border-gray-300 accent-emerald-600 focus:ring-emerald-500 cursor-pointer flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div onClick={() => navigate(`/outreach/${c.id}`)} className="text-sm font-semibold text-gray-900 truncate cursor-pointer hover:text-emerald-700 hover:underline" title={c.name}>{c.name}</div>
                        <div className="text-[10px] text-gray-400 mt-0.5">{channelOf(c)} · {c.step_count || 0} steps · by {c.created_by_name || 'Unknown'}</div>
                      </div>
                      <span className={`flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors[c.status] || statusColors.draft}`}>{c.status}</span>
                    </div>
                    {/* Metrics grid */}
                    <div className="grid grid-cols-4 gap-2 pt-1">
                      <div className="text-center"><div className="text-sm font-semibold text-gray-900 tabular-nums">{enrolled}</div><div className="text-[9px] text-gray-400 uppercase tracking-wide">In camp.</div></div>
                      <div className="text-center"><div className="text-sm font-semibold text-blue-600 tabular-nums">{pct(opened, messaged)}</div><div className="text-[9px] text-gray-400 uppercase tracking-wide">Open</div></div>
                      <div className="text-center"><div className="text-sm font-semibold text-green-600 tabular-nums">{pct(replied, messaged)}</div><div className="text-[9px] text-gray-400 uppercase tracking-wide">Reply</div></div>
                      <div className="text-center"><div className="text-sm font-semibold text-purple-600 tabular-nums">{pct(clicked, messaged)}</div><div className="text-[9px] text-gray-400 uppercase tracking-wide">Click</div></div>
                    </div>
                    {/* Sparkline + messaged count */}
                    <div className="flex items-center justify-between border-t border-gray-100 pt-2">
                      <span className="text-[11px] text-gray-400">{messaged} messaged</span>
                      <Sparkline data={c.spark_sent} color="#16A34A" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex-1 min-h-0 mx-3 mb-8 rounded-lg border shadow-sm flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--list-block-bg)' }}>
            {/* Header row — fixed at the top of the box (not sticky; a real sibling) */}
            <div className="flex-shrink-0 grid items-center gap-2 pl-4 pr-3 py-2 text-[10px] font-medium uppercase tracking-wide rounded-t-lg"
              style={{ gridTemplateColumns: gridTemplate, backgroundColor: 'var(--list-header-bg)', color: 'var(--list-header-text)', borderBottom: '1px solid var(--list-header-border)' }}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 rounded border-gray-300 accent-emerald-600 focus:ring-emerald-500 cursor-pointer" />
              <span>Campaign</span>
              {visibleCols.trend && <span>Trend</span>}
              {visibleCols.owner && <SortHeader col="owner" label="Owner" align="left" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />}
              {visibleCols.created && <SortHeader col="created" label="Created" align="left" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />}
              {visibleCols.enrolled && <SortHeader col="enrolled" label="In campaign" align="right" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />}
              {visibleCols.messaged && <SortHeader col="messaged" label="Messaged" align="right" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />}
              {visibleCols.open && <SortHeader col="open" label="Open" align="right" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />}
              {visibleCols.reply && <SortHeader col="reply" label="Reply" align="right" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />}
              {visibleCols.click && <SortHeader col="click" label="Click" align="right" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />}
              <span />
            </div>
            {/* Only the rows scroll, inside the fixed box */}
            <div className="flex-1 overflow-y-auto">
            {sortedCampaigns.map((c, idx) => {
              const enrolled = c.total_enrolled || 0;
              const messaged = c.messaged_count || 0;
              const opened = c.opened_count || 0;
              const clicked = c.clicked_count || 0;
              const replied = c.reply_events || 0;
              return (
                <div key={c.id}
                  className={`grid items-center gap-2 pl-4 pr-3 py-2.5 border-b last:border-b-0 transition-colors group ${selected.has(c.id) ? 'bg-emerald-100/60' : 'bg-white/60 hover:bg-emerald-50/50'}`}
                  style={{ gridTemplateColumns: gridTemplate }}>
                  <input type="checkbox" checked={selected.has(c.id)}
                    onChange={(e) => toggleWithShift(c.id, idx, e.nativeEvent.shiftKey, sortedCampaigns)}
                    className="w-4 h-4 rounded border-gray-300 accent-emerald-600 focus:ring-emerald-500 cursor-pointer" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span onClick={() => navigate(`/outreach/${c.id}`)} className="text-sm font-semibold text-gray-900 truncate cursor-pointer hover:text-emerald-700 hover:underline">{c.name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${statusColors[c.status] || statusColors.draft}`}>{c.status}</span>
                    </div>
                    <div className="text-[11px] text-gray-400 truncate">
                      {channelOf(c)} · {c.step_count || 0} steps · by {c.created_by_name || 'Unknown'}
                    </div>
                  </div>
                  {visibleCols.trend && <Sparkline data={c.spark_sent} />}
                  {visibleCols.owner && (
                    <div className="flex items-center" title={c.created_by_name || 'Unknown'}>
                      {c.created_by_avatar
                        ? <img src={c.created_by_avatar} alt={c.created_by_name || ''} className="w-6 h-6 rounded-full object-cover border border-gray-200" />
                        : <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold flex items-center justify-center border border-emerald-200">
                            {(c.created_by_name || '?').trim().charAt(0).toUpperCase()}
                          </span>}
                    </div>
                  )}
                  {visibleCols.created && (
                    <span className="text-[11px] text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' }) : '—'}</span>
                  )}
                  {visibleCols.enrolled && <Metric value={enrolled} sub={c.active_sequences > 0 ? `${c.active_sequences} active` : null} />}
                  {visibleCols.messaged && <Metric value={messaged} sub={pct(messaged, enrolled)} />}
                  {visibleCols.open && <Metric value={pct(opened, messaged)} sub={`${opened}`} tone="text-blue-600" />}
                  {visibleCols.reply && <Metric value={pct(replied, messaged)} sub={`${replied}`} tone="text-green-600" />}
                  {visibleCols.click && <Metric value={pct(clicked, messaged)} sub={`${clicked}`} tone="text-purple-600" />}
                  <span />
                </div>
              );
            })}
            </div>
          </div>
        )}
        </>)}
      </div>{/* end content column */}

      {/* Global Settings — right sidebar, pushes content left (flex sibling) */}
      {showScheduleSettings && (
        <div className="flex-shrink-0 w-[340px] bg-white border-l border-gray-200 flex flex-col animate-[slidein_180ms_ease-out] overflow-hidden">
          <style>{`@keyframes slidein { from { width: 0; opacity: 0;} to { width: 340px; opacity: 1;} }`}</style>
          <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Global Settings</h3>
            <button onClick={() => setShowScheduleSettings(false)} className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Scheduling */}
            <div>
              <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Scheduling</div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Minimum minutes between any two sends (firm-wide)</label>
                  <input type="number" min={0} max={1440} value={firmSettings?.master_gap_minutes ?? ''}
                    onChange={e => setFirmSettings(s => ({ ...s, master_gap_minutes: parseInt(e.target.value) || 0 }))}
                    className="w-28 px-2 py-1.5 text-sm border border-gray-300 rounded" />
                  <p className="text-[10px] text-gray-400 mt-1">The whole firm won't send two messages closer than this apart.</p>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Quiet hours start</label>
                    <input type="time" value={firmSettings?.quiet_start?.slice(0,5) || '20:00'}
                      onChange={e => setFirmSettings(s => ({ ...s, quiet_start: e.target.value }))}
                      className="px-2 py-1.5 text-sm border border-gray-300 rounded" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Quiet hours end</label>
                    <input type="time" value={firmSettings?.quiet_end?.slice(0,5) || '08:00'}
                      onChange={e => setFirmSettings(s => ({ ...s, quiet_end: e.target.value }))}
                      className="px-2 py-1.5 text-sm border border-gray-300 rounded" />
                  </div>
                </div>
                <p className="text-[10px] text-gray-400">No sends fire during quiet hours — they roll to the next allowed time.</p>
              </div>
            </div>
            {/* Compliance & tracking — coming soon */}
            <div>
              <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                Compliance &amp; Tracking
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium normal-case tracking-normal">Coming soon</span>
              </div>
              <div className="space-y-2.5 opacity-60 pointer-events-none">
                {[
                  'Default email signature',
                  'Enforce unsubscribe link',
                  'Allow click tracking',
                  'Allow open tracking',
                  'Log LinkedIn replies',
                ].map(opt => (
                  <label key={opt} className="flex items-center gap-2 text-sm text-gray-600">
                    <input type="checkbox" disabled className="w-4 h-4 rounded border-gray-300 accent-emerald-600" />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
            <button onClick={() => setShowScheduleSettings(false)} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button onClick={saveFirmSettings} className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700">Save</button>
          </div>
        </div>
      )}

      {/* New Campaign — right sidebar, pushes content left (flex sibling) */}
      {showCreateModal && (
        <div className="flex-shrink-0 w-[340px] bg-white border-l border-gray-200 flex flex-col animate-[slidein_180ms_ease-out] overflow-hidden">
          <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">New Campaign</h3>
            <button onClick={() => setShowCreateModal(false)} className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && createCampaign()}
                placeholder="e.g. VP Engineering Initial Outreach"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" autoFocus />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
              <textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Brief description of the campaign purpose..."
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none" rows={3} />
            </div>
          </div>
          <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
            <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button onClick={createCampaign} disabled={!newName.trim() || creating}
              className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
              {creating ? 'Creating...' : 'Create & Build Steps'}
            </button>
          </div>
        </div>
      )}

      </div>
    </AppShell>
  );
};

export default OutreachPage;
